import {
  type QueryClient,
  queryOptions,
  useMutation,
  useQueryClient,
  useSuspenseQueries,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { create, keyResolver, windowScheduler } from "@yornaath/batshit";
import type { DateBody, Mortgage, MortgagePatchBody } from "shared";
import { Api } from "@/api.config";
import { QueryKeys } from "@/LinkGen";

const batcher = create({
  fetcher: async (IDs: number[]) => {
    return Api.Mortgage(IDs);
  },
  resolver: keyResolver("ID"),
  scheduler: windowScheduler(50),
});

const _queryOptions = (ID: number) =>
  queryOptions({
    queryKey: QueryKeys.Mortgage(ID),
    queryFn: () => batcher.fetch(ID),
  });

export function useMortgageList() {
  const { data } = useSuspenseQuery({
    queryKey: QueryKeys.Mortgage.List(),
    queryFn: () => Api.MortgageList().then((res) => res.data),
  });

  return data;
}

export function useMortgagesSuspense(IDs: number[]): Mortgage[] {
  return useSuspenseQueries({
    queries: IDs.map((ID) => _queryOptions(ID)),
    combine: (results) => results.map((x) => x.data),
  });
}

export function useMortgage(ID: number) {
  const { data } = useSuspenseQuery(_queryOptions(ID));

  return data;
}

export function useMortgagePropertyInsuranceEdit(mortgageID: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: DateBody) => {
      await Api.MortgageEditPropertyInsuranceDate(mortgageID, body);
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: QueryKeys.Mortgage(mortgageID),
      });
    },
  });
}

export async function invalidateMortgagesWithAddressID(
  queryClient: QueryClient,
  addressID: number,
) {
  const queries = queryClient
    .getQueryCache()
    .findAll({ queryKey: QueryKeys.Mortgage.List() });

  for (const q of queries) {
    const data = q.state.data as Mortgage | undefined;
    if (data?.addressID === addressID) {
      await queryClient.invalidateQueries({ queryKey: q.queryKey });
    }
  }
}

export function useMortgagePatch(mortgageID: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: MortgagePatchBody) =>
      Api.MortgagePatch(mortgageID, body),
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: QueryKeys.Mortgage(mortgageID),
      });
    },
  });
}

export function useMortgageDelete() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (mortgageID: number) => {
      await Api.MortgageDelete(mortgageID);

      await queryClient.refetchQueries({
        queryKey: QueryKeys.Mortgage.List(),
      });
    },
  });

  return mutation.mutateAsync;
}
